/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **A shared expression has no surface, so it declares what it reads** (`X18`, `XS5`, `#1066`).
 *
 * `X14`/`X15` scope a mapping by the surface it is authored on, and `X5` makes a named expression
 * reachable from many surfaces at once — so *the surface decides* has nobody to ask. The answer is
 * to declare from the other end: **the surface declares what it offers, the expression declares what
 * it reads, and usability is the intersection.**
 *
 * Three properties, and each is the reason this is worth having rather than a filter:
 *
 * - **The requirement is derived from the expression's own paths, never hand-declared.** A declared
 *   one would be the twelfth hand-kept list and would drift from the expression it describes on the
 *   first edit.
 * - **The claim is negative, and only ever negative** (`E1`). *Usable here* is not something we can
 *   say — a path can resolve and still be the wrong value. *Reads `response.*`, which an outbound
 *   request does not have* is something we can.
 * - **It is not a permission.** Whether an operator may use an expression is `#904`'s question.
 *
 * ## The surface names its context rather than listing roots
 *
 * `RESOLVE_CONTEXT_BUILDERS` already declares which roots each construction site sets, and
 * `context-roots.spec.ts` checks that declaration against the real code. A surface that listed its
 * own roots would be a second copy of that — a hand-kept list of something we already declare, which
 * is the shape `#1189` and `§6a` are both about. So a surface says *which context my values resolve
 * against*, and what it offers falls out.
 */

import { canonicalizeContextPath, fanOutAt, RESOLVE_CONTEXT_BUILDERS, sharedAcrossFanOut } from '@context-roots';
import type { MacroExpression } from '../../../types/decorator-expression';
import { walkExpression } from './expression-walk';
import { resolutionSiteOf } from '@expression-site';

/** Which resolve context a surface's values are resolved against. Keys of `RESOLVE_CONTEXT_BUILDERS`. */
export type ResolveSurface = keyof typeof RESOLVE_CONTEXT_BUILDERS;

/**
 * **One context, or every context a value here may resolve against** (`R-d`, `#1535`). A round
 * override resolves in the driver or, where it reads what a driver is not handed, in plan
 * construction (`RT4`), so its surface names both. A value is usable where every root it reads is set
 * by one of them and it does not need both at once (`Q9`).
 */
export type ResolveSurfaces = ResolveSurface | readonly ResolveSurface[];

function surfacesOf(surface: ResolveSurfaces | null): readonly ResolveSurface[] {
  if (!surface) return [];
  return typeof surface === 'string' ? [surface] : surface;
}

/**
 * Every context root an expression reads, canonicalised and de-duplicated.
 *
 * It reads `calculated` nodes at any depth — a condition's operand, a branch, a group — through the
 * one walk over an expression (`expression-walk.ts`), which visits what the engine reads: a
 * producer's kept operands and a suppression's carried value are not read, so they are not roots
 * this expression needs. Canonicalised because
 * `user.deviceType` and `session.user.deviceType` are the same root and only one of them is spelled
 * like it (`#1213`).
 */
export function rootsRead(expression: MacroExpression | undefined): string[] {
  const roots = new Set<string>();
  walkExpression(expression, {
    value: (node) => {
      // A pre-`E3` `{type:'path'}` operand and a pre-`E2b` condition `path`/`valuePath` both arrive
      // here as `calculated`: the walk reads them through `readComputedOperands` and
      // `readConditionOperands`. This used to match both spellings by hand, because an untyped walk
      // saw the stored shape — and matching only the new one had made an expression authored before
      // 2026-09-09 read as reading **nothing** (`#1296`). One reader per store, now in the walk.
      if (node.type === 'calculated' && node.path.trim() !== '') {
        const root = canonicalizeContextPath(node.path.trim()).split('.')[0];
        if (root) roots.add(root);
      }
    },
    // A list read member by member is still read from its root (`CO8`, `2e`).
    collection: (operand) => {
      const root = canonicalizeContextPath(operand.path.trim()).split('.')[0];
      if (root) roots.add(root);
    },
  });
  return [...roots];
}

/**
 * The roots this expression reads that the surface does not offer, or `[]` where it can resolve.
 *
 * **An empty array is not a claim that it works** — see the negative-claim rule above. It is the
 * absence of a reason it cannot.
 */
export function rootsMissingOn(expression: MacroExpression | undefined, surface: ResolveSurfaces | null): string[] {
  const surfaces = surfacesOf(surface);
  if (surfaces.length === 0) return [];
  const offered = new Set<string>(surfaces.flatMap((s) => RESOLVE_CONTEXT_BUILDERS[s]?.sets ?? []));
  return rootsRead(expression).filter((root) => !offered.has(root));
}

/**
 * Why this expression cannot resolve here, in the operator's words, or `null`.
 *
 * **One sentence, shared** — `#1187`: where two surfaces make the same decision they share the
 * sentence, not just the function, because a duplicated explanation produces a different sentence
 * and nothing checks it. It names the root rather than the mechanism, because *what is missing* is
 * the thing an operator can act on (`#1045`).
 */
export function describeUnusableHere(
  expression: MacroExpression | undefined,
  surface: ResolveSurfaces | null,
): string | null {
  const missing = rootsMissingOn(expression, surface);
  if (missing.length === 0) {
    // On a surface that routes between sites, the second way to fail is needing both at once (`Q9`).
    if (surfacesOf(surface).length < 2) return null;
    return resolutionSiteOf(expression, null).reason ?? null;
  }
  const list = missing.map((root) => `${root}.*`).join(' and ');
  return `Reads ${list}, which is not available where this value is resolved. It would resolve to nothing here.`;
}

/**
 * **Is everything this expression reads one value for the whole response?** — `X56a` (`#1296`).
 *
 * The surface says it runs once per ad; this is the row-level exception to that — a mapping whose
 * every input is the pod, the demand source or the session produces the *same* value on all N of
 * them, which is a thing an operator comparing `response.price` against `demandSource.floorCpm`
 * has no way to see.
 *
 * **Three answers, and `undefined` is the load-bearing one.** It means nothing can be said, and it
 * covers three different cases that must not be reported as *this varies*:
 *
 * - the surface does not repeat at all, so there is no fan-out to be shared across;
 * - the expression reads no path — a literal is neither shared nor per-ad;
 * - the expression holds a **reference**, whose own paths are not in this tree. The console keeps
 *   the address and only `getForRuntime` substitutes the expression (`X5`), so answering from what
 *   is visible here would be a claim about an expression we have not read — *a value reads as
 *   proof* (`#1002`), and the false direction is the confident one.
 */
export function sharedAcrossFanOutOn(
  expression: MacroExpression | undefined,
  surfaces: ResolveSurfaces | null,
): boolean | undefined {
  // A fan-out is one builder's; a value that may resolve in several places has no single answer.
  const list = surfacesOf(surfaces);
  const surface = list.length === 1 ? list[0] : null;
  if (!surface || !fanOutAt(surface)) return undefined;
  let holdsReference = false;
  walkExpression(expression, {
    value: (node) => {
      if (node.type === 'reference') holdsReference = true;
    },
  });
  if (holdsReference) return undefined;
  const roots = rootsRead(expression);
  if (roots.length === 0) return undefined;
  return roots.every((root) => sharedAcrossFanOut(surface, root) === true);
}
